import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Grid,
  Skeleton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { Article, CheckCircle, Visibility, VisibilityOff, Close } from '@mui/icons-material'
import { useDocumentsManagement } from '../../../hooks/useDocumentsManagement'
import { useResponsivePageSize } from '../../../hooks/useResponsivePageSize'
import DocumentCard from './DocumentCard'
import DocumentsFiltersBar from './DocumentsFiltersBar'
import Pagination from '../../../../Pagination'
import ItemsPerPageSelector from '../../../../ItemsPerPageSelector'

const themeColors = {
  primary: '#232536',
}

const UNDO_WINDOW_MS = 5000

const formatCount = (value) => (Number(value) || 0).toLocaleString()

const CardSkeleton = () => (
  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, height: 220 }}>
    <Skeleton width='40%' height={22} sx={{ mb: 1 }} />
    <Skeleton width='100%' height={20} sx={{ mb: 0.5 }} />
    <Skeleton width='100%' height={20} sx={{ mb: 0.5 }} />
    <Skeleton width='80%' height={20} sx={{ mb: 2 }} />
    <Skeleton width='50%' height={18} sx={{ mb: 1 }} />
    <Skeleton width='100%' height={16} />
  </Box>
)

const EmptyState = ({ seenStatus }) => {
  const message =
    seenStatus === 'NEW'
      ? "You're all caught up - no new documents."
      : seenStatus === 'REVIEWED'
      ? 'No reviewed documents yet.'
      : 'No documents match the current filters.'
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        px: 3,
        border: '2px dashed #e2e8f0',
        borderRadius: 3,
        bgcolor: '#f8fafc',
      }}
    >
      <Article sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
      <Typography variant='h6' sx={{ color: '#475569', fontWeight: 600, mb: 1 }}>
        {seenStatus === 'NEW' ? 'No new documents' : 'No documents found'}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {message}
      </Typography>
    </Box>
  )
}

const statusViews = [
  { value: 'NEW', label: 'New', countKey: 'newCount', dotColor: '#16a34a' },
  { value: 'REVIEWED', label: 'Reviewed', countKey: 'reviewedCount', dotColor: '#94a3b8' },
  { value: 'ALL', label: 'All', countKey: 'totalCount', dotColor: '#23252a' },
]

const ReviewQueueToolbar = ({
  seenStatus,
  counts,
  loading,
  selectedCount,
  visibleCount,
  totalItems,
  allVisibleSelected,
  onStatusChange,
  onSelectAllVisible,
  onClearSelection,
  onBulkMark,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: { xs: 'stretch', md: 'center' },
      justifyContent: 'space-between',
      flexDirection: { xs: 'column', md: 'row' },
      gap: 1.25,
      px: 1.5,
      py: 1.25,
      mb: 1.75,
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      bgcolor: '#f8fafc',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
      {statusViews.map((view) => {
        const active = seenStatus === view.value
        return (
          <Button
            key={view.value}
            onClick={() => onStatusChange(view.value)}
            disableElevation
            size='small'
            sx={{
              minHeight: 38,
              px: 1.35,
              borderRadius: '7px',
              border: '1.5px solid',
              borderColor: active ? '#232536' : '#e2e8f0',
              bgcolor: active ? '#232536' : 'transparent',
              color: active ? '#fff' : '#94a3b8',
              textTransform: 'none',
              display: 'flex',
              gap: 0.9,
              transition: 'border-color 0.15s, background 0.15s, color 0.15s',
              '&:hover': {
                bgcolor: active ? '#232536' : '#f8fafc',
                borderColor: active ? '#232536' : '#cbd5e1',
                color: active ? '#fff' : '#475569',
              },
            }}
          >
            <Box
              component='span'
              sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: active ? '#fff' : view.dotColor, flexShrink: 0 }}
            />
            <Box component='span' sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
              {view.label}
            </Box>
            <Box component='span' sx={{ fontWeight: 700, fontSize: '0.82rem', opacity: active ? 0.75 : 0.55 }}>
              {formatCount(counts[view.countKey])}
            </Box>
          </Button>
        )
      })}
    </Box>

    <Divider flexItem orientation='vertical' sx={{ display: { xs: 'none', md: 'block' }, borderColor: '#e2e8f0' }} />

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'space-between', sm: 'flex-end' },
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, mr: { xs: 0, sm: 0.5 } }}>
        {selectedCount > 0
          ? `${formatCount(selectedCount)} selected`
          : `${formatCount(visibleCount)} shown of ${formatCount(totalItems)}`}
      </Typography>

      {visibleCount > 0 && (
        <Button
          onClick={allVisibleSelected ? onClearSelection : onSelectAllVisible}
          disabled={loading}
          size='small'
          startIcon={<CheckCircle sx={{ fontSize: 16, opacity: allVisibleSelected ? 1 : 0.45 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.78rem',
            px: 1.2,
            borderRadius: '7px',
            border: '1px solid #cbd5e1',
            color: '#23252a',
            bgcolor: '#fff',
            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
          }}
        >
          {allVisibleSelected ? 'Clear Page' : 'Select Page'}
        </Button>
      )}

      {selectedCount > 0 && (
        <>
          {seenStatus !== 'REVIEWED' && (
            <Button
              onClick={() => onBulkMark(true)}
              size='small'
              startIcon={<Visibility sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#16a34a',
                color: 'white',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                px: 1.35,
                borderRadius: '7px',
                '&:hover': { bgcolor: '#15803d' },
              }}
            >
              Mark Reviewed
            </Button>
          )}
          {seenStatus !== 'NEW' && (
            <Button
              onClick={() => onBulkMark(false)}
              size='small'
              startIcon={<VisibilityOff sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#fff',
                color: '#23252a',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                px: 1.35,
                borderRadius: '7px',
                border: '1px solid #cbd5e1',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              Mark Unread
            </Button>
          )}
          <Tooltip title='Clear selection'>
            <Button
              onClick={onClearSelection}
              size='small'
              sx={{
                minWidth: 34,
                width: 34,
                height: 34,
                p: 0,
                color: '#64748b',
                border: '1px solid #e2e8f0',
                bgcolor: '#fff',
                borderRadius: '7px',
                '&:hover': { bgcolor: '#f1f5f9', color: '#23252a' },
              }}
            >
              <Close sx={{ fontSize: 17 }} />
            </Button>
          </Tooltip>
        </>
      )}
    </Box>
  </Box>
)

const DocumentsTab = ({ showNotification }) => {
  // DocumentsTab grid: xs=1col, sm=2col, md=3col, lg=4col
  const { pageSize: responsivePageSize, options: pageSizeOptions } = useResponsivePageSize(
    { xs: 1, sm: 2, md: 3, lg: 4 },
  )

  const {
    documents,
    loading,
    currentPage,
    totalPages,
    totalItems,
    filters,
    pageSize,
    counts,
    loadDocuments,
    handleFilterChange,
    handleFiltersChange,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    resetFilters,
    handleSeenStatusChange,
    markDocumentSeen,
    bulkMarkDocumentsSeen,
  } = useDocumentsManagement(responsivePageSize)

  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [undoState, setUndoState] = useState(null) // { ids: [...], previousSeen: bool, message: string }

  useEffect(() => {
    handlePageSizeChange(responsivePageSize)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsivePageSize])

  useEffect(() => {
    loadDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Clear selection when the visible page changes / filter changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [
    currentPage,
    filters.countries,
    filters.dateFrom,
    filters.dateTo,
    filters.documentType,
    filters.nullDates,
    filters.search,
    filters.seenStatus,
    filters.source,
    filters.taxType,
  ])

  const visibleIds = useMemo(
    () => documents.map((d) => d._id ?? d.id).filter(Boolean),
    [documents],
  )

  const allVisibleSelected = useMemo(
    () => visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id)),
    [selectedIds, visibleIds],
  )

  const toggleSelect = useCallback((id) => {
    setSelectedIds((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(visibleIds))
  }, [visibleIds])

  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])

  const triggerUndo = useCallback((ids, previousSeen, message) => {
    setUndoState({ ids, previousSeen, message })
  }, [])

  const handleSingleMark = useCallback(async (id, seen) => {
    try {
      await markDocumentSeen(id, seen)
      triggerUndo([id], !seen, seen ? 'Marked as reviewed' : 'Marked as unread')
    } catch (e) {
      showNotification?.('Failed to update document status', 'error')
    }
  }, [markDocumentSeen, showNotification, triggerUndo])

  const handleBulkMark = useCallback(async (seen) => {
    const ids = Array.from(selectedIds)
    if (!ids.length) return
    try {
      await bulkMarkDocumentsSeen(ids, seen)
      clearSelection()
      triggerUndo(
        ids,
        !seen,
        `${ids.length} document${ids.length !== 1 ? 's' : ''} ${seen ? 'marked as reviewed' : 'marked as unread'}`,
      )
    } catch (e) {
      showNotification?.('Bulk update failed', 'error')
    }
  }, [bulkMarkDocumentsSeen, clearSelection, selectedIds, showNotification, triggerUndo])

  const handleUndo = useCallback(async () => {
    if (!undoState) return
    const { ids, previousSeen } = undoState
    setUndoState(null)
    try {
      await bulkMarkDocumentsSeen(ids, previousSeen)
      await loadDocuments(currentPage)
    } catch (e) {
      showNotification?.('Undo failed', 'error')
    }
  }, [bulkMarkDocumentsSeen, currentPage, loadDocuments, showNotification, undoState])

  const seenStatus = filters.seenStatus ?? 'NEW'
  const selectedCount = selectedIds.size

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700, color: themeColors.primary }}>
            Government Documents
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
            Documents from government and regulatory authorities.
            {!loading && (
              <Box component='span' sx={{ ml: 0.75 }}>
                <Box component='span' sx={{ fontWeight: 700, color: '#16a34a' }}>
                  {formatCount(counts.newCount)} New
                </Box>
                {' · '}
                <Box component='span' sx={{ fontWeight: 700, color: '#64748b' }}>
                  {formatCount(counts.reviewedCount)} Reviewed
                </Box>
              </Box>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <DocumentsFiltersBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onReset={resetFilters}
      />

      <ReviewQueueToolbar
        seenStatus={seenStatus}
        counts={counts}
        loading={loading}
        selectedCount={selectedCount}
        visibleCount={visibleIds.length}
        totalItems={totalItems}
        allVisibleSelected={allVisibleSelected}
        onStatusChange={handleSeenStatusChange}
        onSelectAllVisible={selectAllVisible}
        onClearSelection={clearSelection}
        onBulkMark={handleBulkMark}
      />

      {/* Content */}
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : documents.length === 0 ? (
        <EmptyState seenStatus={seenStatus} />
      ) : (
        <Grid container spacing={2}>
          {documents.map((doc, i) => {
            const id = doc._id ?? doc.id
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={id ?? `idx-${i}`}>
                <DocumentCard
                  document={doc}
                  selected={id != null && selectedIds.has(id)}
                  onToggleSelect={toggleSelect}
                  onMarkSeen={handleSingleMark}
                />
              </Grid>
            )
          })}
        </Grid>
      )}

      {/* Pagination */}
      {totalItems > 0 && (
        <Box sx={{ mt: 2 }}>
          {totalPages > 1 && (
            <Pagination
              itemsPerPage={pageSize}
              totalItems={totalItems}
              currentPage={currentPage}
              paginate={handlePageChange}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, px: 0.5 }}>
            <ItemsPerPageSelector
              itemsPerPage={pageSize}
              setItemsPerPage={handlePageSizeChange}
              loading={loading}
              options={pageSizeOptions}
            />
          </Box>
        </Box>
      )}

      {/* Undo snackbar */}
      <Snackbar
        open={!!undoState}
        autoHideDuration={UNDO_WINDOW_MS}
        onClose={() => setUndoState(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={undoState?.message ?? ''}
        action={
          <>
            <Button
              size='small'
              onClick={handleUndo}
              sx={{ color: '#fde68a', fontWeight: 700, textTransform: 'none' }}
            >
              UNDO
            </Button>
            <Button
              size='small'
              onClick={() => setUndoState(null)}
              sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 0, ml: 0.5 }}
            >
              <Close sx={{ fontSize: 18 }} />
            </Button>
          </>
        }
      />
    </Box>
  )
}

export default DocumentsTab
